import { Instagram, ArrowRight } from "lucide-react";
import { INSTAGRAM_URL, INSTAGRAM_HANDLE } from "../../lib/social";

/**
 * "Follow us on Instagram" homepage section.
 *
 * Uses a 6-tile strip of featured artworks/images styled like an Instagram grid,
 * each linking to the public profile. A real IG API embed can be swapped in
 * later — for now this is a static, deps-free showcase.
 */
export const InstagramSection = ({ tiles = [] }) => {
  // Fall back to a sensible set if no tiles passed in.
  const items = tiles.slice(0, 6);
  // Pad to 6 with placeholders by repeating the first available tile.
  while (items.length > 0 && items.length < 6) {
    items.push({ ...items[items.length % items.length], _pad: true });
  }

  if (items.length === 0) return null;

  return (
    <section
      data-testid="instagram-section"
      className="px-6 md:px-12 lg:px-24 py-20 bg-white border-y border-[#E5E0D8]"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <p className="label-eyebrow text-[#1E3A8A] inline-flex items-center gap-2">
            <Instagram className="w-4 h-4" strokeWidth={1.5} />
            Follow us on Instagram
          </p>
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight font-medium mt-3 text-[#111]">
            {INSTAGRAM_HANDLE}
          </h2>
          <p className="text-[#555] mt-3 max-w-xl">
            Studio visits, in-progress works, and dispatches from the AfricaArtMasters network — straight to your feed.
          </p>
        </div>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="instagram-cta"
          className="label-eyebrow inline-flex items-center gap-2 border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-3 self-start"
        >
          Follow on Instagram <ArrowRight className="w-3 h-3" />
        </a>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {items.map((item, i) => (
          <a
            key={`${item.id || "tile"}-${i}`}
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`instagram-tile-${i}`}
            className="group relative block aspect-square overflow-hidden bg-[#F2EDE5]"
            aria-label={`Open AfricaArtMasters on Instagram`}
          >
            <img
              src={item.image_url || item.img}
              alt={item.title || "@africaartmasters — contemporary African art on Instagram"}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-[#1E3A8A]/0 group-hover:bg-[#1E3A8A]/70 transition-colors flex items-center justify-center">
              <Instagram
                className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                strokeWidth={1.5}
              />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
};
